<template>
  <v-layout>
    <ClassicScripts />
    <Tracking />
    <v-main>
      <SideMenu
        v-if="isDesktop"
        :leftMenuItems="leftMenuItems"
        :loading="menuLoading"
        :memberData="memberData"
        :myRankingsData="myRankingsData"
        :isLoggedIn="isLoggedIn"
        :isLoggedOut="isLoggedOut"
        :ngb="ngb" />
      <MobileTopBar v-if="isMobile || isTablet" :isLoggedIn="isLoggedIn" :isLoggedOut="isLoggedOut" />
      <DesktopTopBar v-if="isDesktop" :isLoggedIn="isLoggedIn" :isLoggedOut="isLoggedOut" />
      <AdminAcceptBanner />
      <GeneralBanner :banners="banners" />
      <ToastMessages />
      <ModalPopup
        :title="modalStore.title ?? ''"
        :description="modalStore.description ?? ''"
        :pageComponent="modalStore.component"
        :isVisible="modalStore.isVisible"
        :pageComponentProps="modalStore.componentProps"
        :color="modalStore.componentProps.color"
        :noPadding="modalStore.noPadding"
        :closeIcon="modalStore.closeIcon"
        @update:modelValue="modalStore.closeModal" />
      <PullDownRefresh v-if="isNativeApp">
        <RouterView />
      </PullDownRefresh>
      <RouterView v-else />
      <BottomSheet
        v-model="bottomSheetStore.isOpen"
        :id="bottomSheetStore.id || ''"
        :bg-color="bottomSheetStore.bgColor"
        :component="bottomSheetStore.component"
        :title="bottomSheetStore.title || ''"
        :site-logo="bottomSheetStore.siteLogo"
        :show-on-desktop="bottomSheetStore.showOnDesktop"
        :disable-persistence="bottomSheetStore.disablePersistence" />
      <MobilePanel v-if="isMobile || isTablet" :loading="menuLoading" />
      <MobileMenu v-if="isMobile || isTablet" />
      <SiteFooter v-if="isDesktop" :memberData="memberData" :myRankingsData="myRankingsData" :moreData="moreData" />
      <ZendeskHelpButton v-if="!isMobile" />
      <LanguageSwitcher v-if="(isMobile || isTablet) && !isNativeApp" />
      <HybridBlocks v-if="isNativeApp" />
    </v-main>
  </v-layout>
</template>

<script lang="ts" setup>
import { useTheme } from 'vuetify';
import { useRoute } from 'vue-router';
import { useModalStore } from '@/store/modalStore';
import { useUserStore } from '@/store/userJWTStore';
import { useUserDataStore } from '@/store/userDataStore';
import { SafeArea } from '@capacitor-community/safe-area';
import { useBottomSheetStore } from '@/store/bottomSheetStore';
import { isMobileApp } from '@/services/mobile_app/mobileAppServices';
import { ListenForPageRefresh } from '@/services/tools/RefreshService';
import { sportTitle, getResourceUrl, isMobile, isTablet, isDesktop, updateViewport } from '@/services/admin_actions/apiUtils';
import { onMounted, onBeforeUnmount, onBeforeMount, computed, watch, ref } from 'vue';
import { registerPushNotifications } from '@/services/notifications/PushNotificationService';

import SideMenu from './navigation/SideMenu.vue';
import SiteFooter from './navigation/SiteFooter.vue';
import MobileMenu from './navigation/MobileMenu.vue';
import MobilePanel from './navigation/MobilePanel.vue';
import MobileTopBar from './navigation/MobileTopBar.vue';
import DesktopTopBar from './navigation/DesktopTopBar.vue';
import Tracking from '@/components/GlobalComponents/Tracking.vue';
import ModalPopup from '@/components/ComponentLibrary/ModalPopup.vue';
import BottomSheet from '@/components/ComponentLibrary/BottomSheet.vue';
import HybridBlocks from '@/components/GlobalComponents/HybridBlocks.vue';
import ToastMessages from '@/components/FeedBackComponents/ToastMessages.vue';
import ClassicScripts from '@/components/GlobalComponents/ClassicScripts.vue';
import GeneralBanner from '@/components/NotificationBanners/GeneralBanner.vue';
import PullDownRefresh from '@/components/GlobalComponents/PullDownRefresh.vue';
import ZendeskHelpButton from '@/components/GlobalComponents/ZendeskHelpButton.vue';
import AdminAcceptBanner from '@/components/NotificationBanners/AdminAcceptBanner.vue';
import LanguageSwitcher from '@/components/SingleElements/LanguageSwitcher.vue';

// Define the theme
const theme = useTheme();
const themeName = import.meta.env.VITE_SPORT_THEME;
theme.global.name.value = themeName;

// Define computed properties for user data
const ngb = computed(() => userDataStore.menuData.site_map?.NGB);
const banners = computed(() => userDataStore.banners);
const leftMenuItems = computed(() => userDataStore.menuData.left_menu_items);
const memberData = computed(() => userDataStore.menuData.site_map['Members area']);
const myRankingsData = computed(() => userDataStore.menuData.site_map['My rankings']);
const moreData = computed(() => userDataStore.menuData.site_map['About']);

// Function to Show/Hide components in child components
const store = useUserStore();
const isLoggedIn = computed(() => store.isLoggedIn);
const isLoggedOut = computed(() => store.isLoggedOut);

// Add functions to control the popup element stores
const modalStore = useModalStore();
const bottomSheetStore = useBottomSheetStore();

// Ref for viewport width
const viewportWidth = ref(window.innerWidth);
watch(viewportWidth, () => updateViewport());

// Pinia Store
const userDataStore = useUserDataStore();
onBeforeMount(async () => {
  await userDataStore.fetchUserData().then(() => {
    userDataStore.checkForTriggers();
  });
});

// Watcher to monitor changes in leftMenuItems
const menuLoading = ref(true);
watch(leftMenuItems, () => {
  menuLoading.value = false;
});

// Watch for route changes and content changes
const route = useRoute();
watch([() => route.fullPath, () => userDataStore.menuData], () => {}, { deep: true });

// Function to determine if the device is mobile app
const nativeAppDetected = ref(false);
const isNativeApp = computed(() => nativeAppDetected.value);

// Function to handle window resize
const handleResize = () => {
  viewportWidth.value = window.innerWidth;
};

// Initialize on mount
onMounted(() => {
  injectWebPageJsonLd(); // Inject WebPage Schema
  window.addEventListener('resize', handleResize);
  updateViewport(); // Run initial viewport check
  nativeAppDetected.value = isMobileApp(); // Detect if running in native mobile app

  registerPushNotifications(); // Setup push notifications
  ListenForPageRefresh(); // Listen for page refresh events
});

// Cleanup on unmount - remove event listener
onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});

// Add Schema dynamically to the page for WebPage
const injectWebPageJsonLd = () => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';

  // Define the correct description based on the sport
  const sport = sportTitle.value.toLowerCase();
  const description =
    sport === 'padel' || sport === 'tennis'
      ? 'Find your level, track your progress. From the team behind SquashLevels.'
      : `${sportTitle.value}Levels has established itself as ${sport}’s premier tool for measuring performance of global ${sport} players and is backed by the World ${sportTitle.value} Federation.`;

  script.text = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: window.location.origin,
    name: document.title || `${sportTitle.value}Levels – Official ${sportTitle.value} Rankings`,
    description: description,
    image: getResourceUrl('/cdn/' + sportTitle.value.toLowerCase() + '/site/search-background.jpg'),
    publisher: {
      '@type': 'Organization',
      name: `${sportTitle.value}Levels`,
      logo: getResourceUrl('/cdn/' + sportTitle.value.toLowerCase() + '/site/search-background.jpg'),
    },
  });

  document.head.appendChild(script);
};
</script>

<style lang="scss">
// Less Than ≤ 1024px: Show mobile menu, hide community menu
// @media (max-width: $tablet-screen) {
//   .v-main {
//     padding-top: calc(64px + var(--safe-area-inset-top, 0px)) !important;
//     padding-bottom: calc(64px + var(--safe-area-inset-bottom, 0px)) !important;
//   }
// }

// Less Than ≤ 767px: Show mobile menu, hide community menu
@media (max-width: $mobile-screen) {
  .v-main {
    padding-top: calc(58px + var(--safe-area-inset-top, 0px)) !important;
    // padding-bottom: calc(50px + var(--safe-area-inset-bottom, 0px)) !important;
  }
}
</style>
